import React, { useRef } from 'react';
import { Sparkle } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { solutions } from '../mock/data';

const Solutions = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="solutions" className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(139, 92, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(6, 182, 212, 0.1) 0%, transparent 50%)',
            backgroundSize: '100% 100%'
          }}
        />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isInView ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center space-x-2 mb-4"
          >
            <Sparkle className="w-6 h-6 text-purple-600" />
            <span className="px-6 py-2 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 rounded-full border border-purple-300/30 text-purple-700 font-bold text-sm tracking-widest">
              OUR SOLUTIONS
            </span>
            <Sparkle className="w-6 h-6 text-cyan-600" />
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            COMPREHENSIVE{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600">
              SOLUTIONS
            </span>
          </motion.h2>
          
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 mx-auto rounded-full mb-4"
          ></motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto text-justify hyphens-auto"
          >
            Comprehensive payment solutions tailored to your business needs
          </motion.p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="space-y-16 sm:space-y-24 md:space-y-28 lg:space-y-32">
          {solutions.map((solution, index) => (
            <motion.div
              key={solution.id}
              initial={{ opacity: 0, y: 100 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 100 }}
              transition={{ duration: 0.8, delay: 0.2 + index * 0.2 }}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-12 items-center`}
            >
              {/* Image with reduced size */}
              <motion.div
                className="flex-1 w-full max-w-md mx-auto"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="group relative overflow-hidden rounded-2xl shadow-xl">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.6 }}
                    className="relative"
                  >
                    <img
                      src={solution.image}
                      alt={solution.title}
                      className="w-full h-52 sm:h-64 object-cover"
                    />
                  </motion.div>
                  
                  {/* Subtle overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 to-blue-900/40 opacity-60 group-hover:opacity-40 transition-opacity duration-500"></div>
                  
                  {/* Corner frame */}
                  <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-white/50"></div>
                  <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-white/50"></div>
                </div>
              </motion.div>

              {/* Content */}
              <div className="flex-1">
                <motion.div
                  initial={{ opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? 50 : -50 }}
                  transition={{ duration: 0.8, delay: 0.4 + index * 0.2 }}
                >
                  <motion.h3
                    className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4 sm:mb-6"
                  >
                    {solution.title}
                  </motion.h3>
                  
                  <p className="text-slate-700 leading-relaxed mb-6 text-base text-justify hyphens-auto">
                    {solution.description}
                  </p>

                  {/* Features with clean design */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {solution.features.map((feature, idx) => (
                      <motion.div
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                        transition={{ delay: 0.6 + index * 0.2 + idx * 0.1 }}
                        whileHover={{ x: 3, scale: 1.02 }}
                        className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                        <span className="text-slate-700 text-sm font-medium">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solutions;
